import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Box, Typography,
  Stack, Chip, Paper, Grid, CircularProgress, Tooltip,
  Drawer, Button, Divider, ToggleButton, ToggleButtonGroup, Select, MenuItem, TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
  LoginRounded as CheckInIcon,
  LogoutRounded as CheckOutIcon,
  TimelapseRounded as WorkedIcon,
  ScheduleRounded as ShiftIcon,
  RestaurantRounded as FoodIcon,
  CheckCircleRounded as OnTimeIcon,
  WatchLaterRounded as LateIcon,
  MoreTimeRounded as OvertimeIcon,
} from '@mui/icons-material';
import { useEmployeeAttendanceHistory, useEmployeeCycleAttendance, useEmployeeYearSummary, useOverrideAttendance } from '../../hooks/useAttendance';
import { MONTH_NAMES, listCycles, getCycleByParam, getCycleByStart, getCurrentCycle } from '../../utils/attendanceCycle';
import AttendanceInsights, { AttendanceBreakdownChart, YearLeaveSummary, isOnTime, buildAttendanceMetrics, fmtHoursMinutes } from './AttendanceInsights';
import SLUGS from '../../resources/slugs';
import { STATUS_META, AttendanceLegend, metaForStatus } from './attendanceLegend';
import { attendanceSourceLabel } from './attendanceSource';
import { formatDate } from '../../utils/dateFormat';
import { SELECTED_MENU_PROPS } from '../StyledComponents/StyledComponent';

// Primary accent shared with /home, /payslip, /profile … (FORTHEYE soft cards).
const ACCENT = '#2f6bff';
// Tabular-number face — matches the /home dashboard's big numeric values.
const NUM_FONT = "'Space Grotesk', 'Outfit', sans-serif";
// Status dot colour per summary stat — gives the white summary card life.
const STAT_DOT = {
  'Working days': '#2f6bff', Present: '#16a34a', Absent: '#ef4444', Leave: '#ef4444',
  'Half Day': '#eab308', 'On Duty': '#0d9488', 'Comp Off': '#f59e0b', Holiday: '#cbd5e1',
  Attendance: '#16a34a', Food: '#f59e0b', 'LOP days': '#ef4444',
};
// Vivid per-status colours for the employee page (calendar line/dot + legend) —
// same palette as the /home dashboard so both redesigned pages stay in lockstep.
const STATUS_DOT = {
  PRESENT: '#16a34a', ABSENT: '#ef4444', LEAVE: '#ef4444', HALF_DAY: '#eab308',
  ON_DUTY: '#0d9488', COMP_OFF: '#f59e0b', HOLIDAY: '#cbd5e1', NOT_RECORDED: '#cbd5e1',
  HALF_DAY_LEAVE: '#d97706',
};
const LEGEND_COLOR = { P: '#16a34a', A: '#ef4444', L: '#ef4444', OD: '#0d9488', CO: '#f59e0b', H: '#cbd5e1', HD: '#eab308', '½L': '#d97706' };
// Solid white mockup card used across the employee attendance page.
const PAGE_CARD_SX = { p: 2.5, borderRadius: '18px', bgcolor: '#fff', border: '1px solid #eef0f4', boxShadow: '0 1px 2px rgba(16,24,40,.05)' };

// Work duration arrives as a timedelta string like "8:22:00" — show "8:22"
// (drop the seconds). Pass anything unexpected through untouched.
const fmtDur = (raw) => {
  if (!raw) return null;
  const m = String(raw).match(/^(\d+):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : String(raw);
};

// Work duration "8:22:00" → "8h 22m" for the Today-at-a-glance strip.
const fmtHM = (raw) => {
  if (!raw) return '—';
  const m = String(raw).match(/^(\d+):(\d{2})/);
  if (!m) return String(raw);
  const h = Number(m[1]);
  const mm = Number(m[2]);
  if (!h && !mm) return '0m';
  return [h ? `${h}h` : null, (mm || !h) ? `${mm}m` : null].filter(Boolean).join(' ');
};

const formatDayCells = (year, month, days, rangeStartISO, rangeEndISO) => {
  // Build a 6×7 calendar grid where the first row starts on Monday.
  const firstOfMonth = new Date(year, month - 1, 1);
  const startOffset = (firstOfMonth.getDay() + 6) % 7; // Mon=0..Sun=6
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);

  const lastDay = new Date(year, month, 0).getDate();
  for (let d = 1; d <= lastDay; d++) {
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    // When a cycle range is given, days outside [start, end] are not part of the cycle.
    const outOfRange = (rangeStartISO && dateStr < rangeStartISO) || (rangeEndISO && dateStr > rangeEndISO);
    cells.push({ day: d, dateStr, cell: days?.[dateStr] || null, outOfRange });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

// For a 26→25 cycle: build ONE continuous Monday-first grid containing only the
// cycle's days, using the ordered ISO dates the backend returns in `dates`.
const formatCycleCells = (dates, days) => {
  if (!dates || !dates.length) return [];
  const [y, m, d] = String(dates[0]).split('-').map(Number);
  const startOffset = (new Date(y, m - 1, d).getDay() + 6) % 7; // Mon=0..Sun=6
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  dates.forEach((iso) => {
    cells.push({ day: Number(iso.slice(8, 10)), dateStr: iso, cell: days?.[iso] || null, outOfRange: false });
  });
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

const EmployeeAttendanceCalendar = ({
  open, onClose, employeeNo,
  mode = 'month', cycle,
  initialMonth, initialYear, initialViewMode,
  variant = 'dialog',
}) => {
  const navigate = useNavigate();
  const today = new Date();
  const [month, setMonth] = useState(initialMonth || today.getMonth() + 1);
  const [year, setYear] = useState(initialYear || today.getFullYear());
  const [selectedCell, setSelectedCell] = useState(null);
  // Override → Present asks for the actual punch: { in, out, error } while open.
  const [presentForm, setPresentForm] = useState(null);
  // "At a glance" strip: how many days back from today it shows (1 = yesterday).
  const [dayOffset, setDayOffset] = useState(1);

  const isCycleMode = mode === 'cycle';
  // Compact day tiles (day + code + In/Out) in BOTH the inline panel and the
  // full-screen dialog. In the dialog the calendar is width-capped below so the
  // tiles stay small instead of stretching to the full screen width.
  const compact = true;

  // The admin dialog AND the employee full "page" both get the "View period"
  // selector (Cycle / Month / Year + a specific period) that re-scopes the
  // ENTIRE view, plus the donut + year summary. Only the compact employee inline
  // panel keeps the original cycle-home view whose arrows page out to a month.
  const isAdminDialog = variant !== 'inline';
  // Manual attendance override is admin-only — it must never appear in the
  // employee's own views (inline panel or full page).
  const allowOverride = variant === 'dialog';
  const [viewMode, setViewMode] = useState(initialViewMode || 'cycle'); // cycle | month
  const [activeCycle, setActiveCycle] = useState(cycle || getCurrentCycle().cycle);
  const [browsingMonth, setBrowsingMonth] = useState(false);

  const periodKind = isAdminDialog
    ? viewMode
    : (isCycleMode && !browsingMonth ? 'cycle' : 'month');
  const wantCycle = periodKind === 'cycle';
  const wantMonth = periodKind === 'month';
  const wantYear = periodKind === 'year';
  const showCycle = wantCycle;     // calendar grid + headers
  const isCycle = wantCycle;       // legacy alias used in the render
  const activeCycleParam = isAdminDialog ? activeCycle : cycle;

  // Year view is year-to-date for the current year (future days can't have
  // attendance), and the full year for a past year.
  const yearEnd = year < today.getFullYear()
    ? `${year}-12-31`
    : `${year}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // One query per period kind; the others are disabled (null employeeNo).
  const cycleQuery = useEmployeeCycleAttendance(wantCycle ? employeeNo : null, { cycle: activeCycleParam });
  const monthQuery = useEmployeeAttendanceHistory(wantMonth ? employeeNo : null, { month, year });
  const yearQuery = useEmployeeAttendanceHistory(wantYear ? employeeNo : null, { start: `${year}-01-01`, end: yearEnd });

  const data = wantCycle ? cycleQuery.data : wantMonth ? monthQuery.data : yearQuery.data;
  const isLoading = wantCycle ? cycleQuery.isLoading : wantMonth ? monthQuery.isLoading : yearQuery.isLoading;

  // Year-level leave + LOP summary (admin dialog only), keyed to the year shown.
  const { data: yearSummary } = useEmployeeYearSummary(
    isAdminDialog ? employeeNo : null,
    { year: data?.payroll_year || year },
  );
  const { mutate: overrideAttendance, isPending: isOverriding } = useOverrideAttendance();

  // Concrete label for the selected period, shown in the View-period container.
  const periodLabel = wantMonth
    ? `${MONTH_NAMES[month - 1]} ${year}`
    : wantYear
    ? `${year}`
    : (data?.label || getCycleByParam(activeCycleParam).label);

  // Dropdown options for each mode.
  const cycleOptions = listCycles(15);
  const monthOptions = [];
  for (let i = 0; i < 15; i++) {
    const idx = today.getMonth() - i;
    monthOptions.push({ month: ((idx % 12) + 12) % 12 + 1, year: today.getFullYear() + Math.floor(idx / 12) });
  }
  const yearOptions = [0, 1, 2, 3].map((i) => today.getFullYear() - i);

  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); }
    else setMonth(month - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); }
    else setMonth(month + 1);
  };

  // Switching mode pre-selects a sensible period derived from the cycle in view.
  const changeViewMode = (m) => {
    if (!m) return;
    const base = getCycleByParam(activeCycleParam);
    if (m === 'month') { setMonth(base.endMonth); setYear(base.endYear); }   // the cycle's payroll month
    if (m === 'year') { setYear(base.payrollYear); }
    setViewMode(m);
  };

  // Admin period arrows — prev/next of the active cycle, month, or year.
  const adminPrevPeriod = () => {
    if (wantCycle) {
      const b = getCycleByParam(activeCycle);
      const pm = b.startMonth === 1 ? 12 : b.startMonth - 1;
      const py = b.startMonth === 1 ? b.startYear - 1 : b.startYear;
      setActiveCycle(getCycleByStart(pm, py).cycle);
    } else if (wantMonth) { prevMonth(); } else { setYear(year - 1); }
  };
  const adminNextPeriod = () => {
    if (wantCycle) {
      const b = getCycleByParam(activeCycle);
      const nm = b.startMonth === 12 ? 1 : b.startMonth + 1;
      const ny = b.startMonth === 12 ? b.startYear + 1 : b.startYear;
      setActiveCycle(getCycleByStart(nm, ny).cycle);
    } else if (wantMonth) { nextMonth(); } else { setYear(year + 1); }
  };

  // Leaving the cycle home view into a full-month view of one of its edge months.
  const shiftMM = (m, y, delta) => {
    const idx = (m - 1) + delta;
    return { m: ((idx % 12) + 12) % 12 + 1, y: y + Math.floor(idx / 12) };
  };
  const cycleStartMY = () => {
    const [y, m] = String(cycle || '').split('-').map(Number);
    return { m: m || (today.getMonth() + 1), y: y || today.getFullYear() };
  };
  const enterMonthPrev = () => {
    const b = cycleStartMY();           // ‹ from cycle → start month (e.g. May)
    setMonth(b.m); setYear(b.y); setBrowsingMonth(true);
  };
  const enterMonthNext = () => {
    const b = shiftMM(cycleStartMY().m, cycleStartMY().y, 1); // › → end month (e.g. June)
    setMonth(b.m); setYear(b.y); setBrowsingMonth(true);
  };

  const totals = data?.totals || {};
  // Food allowance is HO-file only; non-HO employees always read ₹0 (CRM #44163).
  const foodApplies = totals.food_allowance_applicable !== false;
  const pct = data?.attendance_pct ?? 0;

  const closeDayDrawer = () => {
    setSelectedCell(null);
    setPresentForm(null);
  };

  const handleOverride = (newStatus) => {
    if (!selectedCell?.cell?.id) return;
    // Present needs IN/OUT timings, otherwise the record is incomplete — open the
    // timing form (prefilled with any punch already on the day) instead of saving.
    if (newStatus === 'PRESENT') {
      setPresentForm({ in: selectedCell.cell.in_time || '', out: selectedCell.cell.out_time || '', error: '' });
      return;
    }
    overrideAttendance({
      attendanceId: selectedCell.cell.id,
      payload: { status: newStatus },
    });
    closeDayDrawer();
  };

  // Same checks as the backend (attendance_manual_override) so HR sees them instantly.
  const presentTimingError = ({ in: inTime, out: outTime }) => {
    if (!inTime && !outTime) return 'IN time and OUT time are required.';
    if (!outTime) return 'OUT time is required.';
    if (!inTime) return 'IN time is required.';
    if (inTime === outTime) return 'OUT time must be different from IN time.';
    return '';
  };

  const savePresentOverride = () => {
    if (!selectedCell?.cell?.id || !presentForm) return;
    const error = presentTimingError(presentForm);
    if (error) {
      setPresentForm((f) => ({ ...f, error }));
      return;
    }
    overrideAttendance(
      {
        attendanceId: selectedCell.cell.id,
        payload: { status: 'PRESENT', in_time: presentForm.in, out_time: presentForm.out },
      },
      {
        // The hook refetches the calendar, so the cell shows Present + timings.
        onSuccess: closeDayDrawer,
        onError: (err) => setPresentForm((f) => (f ? { ...f, error: err?.response?.data?.error || 'Could not save. Try again.' } : f)),
      },
    );
  };

  const headerTitle = wantCycle
    ? (data?.label || 'Attendance cycle')
    : wantYear
    ? `Year ${year}`
    : `${MONTH_NAMES[month - 1]} ${year}`;
  const headerSub = wantCycle && data?.payroll_month
    ? `Payroll: ${data.payroll_month} ${data.payroll_year}`
    : null;

  const legendBlock = <AttendanceLegend compact={compact} justify="flex-end" sx={{ justifyContent: { xs: 'flex-start', md: 'flex-end' } }} />;

  const titleBlock = (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant={compact ? 'h6' : 'h5'} fontWeight={800}>
          {data?.name || employeeNo}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {data?.employee_no} · {data?.designation || '—'} · {data?.department || '—'}
        </Typography>
      </Box>
      {/* On the employee's own page the legend lives in the top header bar
          (beside the profile button), so it's omitted from the title row here. */}
      {variant !== 'page' && (
        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          {legendBlock}
        </Box>
      )}
      {onClose && (
        <IconButton onClick={onClose} size={variant === 'inline' ? 'small' : 'large'} sx={{ flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      )}
    </Stack>
  );

  // Stats for the relocated green summary card (sidebar in the full-screen view).
  const statList = [
    { label: 'Working days', value: totals.working_days || 0 },
    { label: 'Present', value: totals.present || 0 },
    { label: 'Absent', value: totals.absent || 0 },
    { label: 'Leave', value: totals.leave || 0 },
    { label: 'Half Day', value: totals.half_day || 0 },
    { label: 'On Duty', value: totals.on_duty || 0 },
    { label: 'Comp Off', value: totals.comp_off || 0 },
    { label: 'Holiday', value: totals.holiday || 0 },
    { label: 'Attendance', value: `${pct}%` },
    { label: 'Food', value: `₹${totals.food_allowance_amount || 0}` },
    // LOP days is a quick entry point to apply for leave — on the employee's own
    // page it links to the Leaves page (with ?apply=1 so the apply form opens on
    // arrival). Gated to the page variant so an admin viewing a report isn't sent
    // to the employee's leave screen.
    ...(isCycle ? [{
      label: 'LOP days',
      value: data?.lop_days ?? 0,
      onClick: variant === 'page' ? () => navigate(`${SLUGS.leaves}?apply=1`) : undefined,
    }] : []),
  ];

  // Employee page → soft white FORTHEYE card (matches the rest of the portal).
  // Admin dialog/inline → original purple gradient (unchanged).
  const isPage = variant === 'page';
  const statsCard = isPage ? (
    <Paper
      elevation={0}
      sx={{
        p: 2.5, borderRadius: '22px', bgcolor: '#fff',
        border: '1px solid #eef0f4', boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
      }}
    >
      <Typography variant="h6" fontWeight={800} sx={{ color: '#111827' }}>{headerTitle}</Typography>
      {headerSub && (
        <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', mb: 0.5 }}>
          {headerSub}
        </Typography>
      )}
      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        {statList.map((s) => (
          <Grid item xs={4} key={s.label}>
            <SummaryStat label={s.label} value={s.value} dot={STAT_DOT[s.label]} onLight />
          </Grid>
        ))}
      </Grid>
    </Paper>
  ) : (
    <Paper
      elevation={0}
      sx={{
        p: 2.5, borderRadius: 3, color: '#fff',
        background: 'linear-gradient(135deg, #4338ca 0%, #6d28d9 100%)',
        boxShadow: '0 12px 30px rgba(67,56,202,0.28)',
      }}
    >
      <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>{headerTitle}</Typography>
      {headerSub && (
        <Typography variant="caption" sx={{ color: '#fff', opacity: 0.85, display: 'block', mb: 0.5 }}>
          {headerSub}
        </Typography>
      )}
      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        {statList.map((s) => (
          <Grid item xs={4} key={s.label}>
            <SummaryStat label={s.label} value={s.value} />
          </Grid>
        ))}
      </Grid>
    </Paper>
  );

  // Employee-page-only "Yesterday at a glance" strip — shows the previous day's
  // punch (today's biometric punches usually aren't uploaded yet). On-time/late
  // mirrors AttendanceInsights (prefers the backend `is_late` flag, falls back to
  // the shift-start heuristic `isOnTime`).
  const todayStrip = (() => {
    const n = new Date();
    n.setDate(n.getDate() - dayOffset); // dayOffset days back from today (1 = yesterday)
    const dayISO = `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
    const c = data?.days?.[dayISO] || null;
    const dayLabel = `${n.toLocaleDateString('en-US', { weekday: 'short' })} ${n.getDate()} ${n.toLocaleDateString('en-US', { month: 'short' })}`;
    const relLabel = dayOffset === 0 ? 'Today' : dayOffset === 1 ? 'Yesterday' : null;
    const worked = fmtHM(c?.work_duration);

    let badge = { label: 'Not checked in', fg: '#6b7280', bg: '#f3f4f6' };
    // Normalise so Absent/Holiday/Leave always resolve regardless of the source's
    // casing (excel / biometric / manual / leave-derived).
    const st = String(c?.status || '').toUpperCase();
    if (st === 'HOLIDAY' || (!c && n.getDay() === 0)) badge = { label: 'Holiday', fg: '#64748b', bg: '#f1f5f9' };
    else if (st === 'LEAVE') badge = { label: 'On leave', fg: '#ef4444', bg: '#fef2f2' };
    else if (st === 'ABSENT') badge = { label: 'Absent', fg: '#ef4444', bg: '#fef2f2' };
    else if (c?.in_time) {
      const onTime = (typeof c.is_late === 'boolean') ? !c.is_late : isOnTime(c.in_time, dayISO);
      badge = onTime === false
        ? { label: 'Late', fg: '#f59e0b', bg: '#fffbeb' }
        : onTime === true
          ? { label: 'On time', fg: '#16a34a', bg: '#f0fdf4' }
          : { label: 'Checked in', fg: ACCENT, bg: '#eef4ff' };
    }

    const foodOk = !!c?.food_allowance_eligible;

    return (
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: '22px', bgcolor: '#fff', border: '1px solid #eef0f4', boxShadow: '0 6px 24px rgba(17,24,39,0.05)' }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.75 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <IconButton size="small" onClick={() => setDayOffset((o) => o + 1)} aria-label="Previous day" sx={{ color: ACCENT }}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <ShiftIcon sx={{ color: ACCENT, fontSize: 18, ml: 0.25 }} />
            <Typography sx={{ fontWeight: 800, color: '#111827' }}>{relLabel ? `${relLabel} · ${dayLabel}` : dayLabel}</Typography>
            <IconButton size="small" onClick={() => setDayOffset((o) => Math.max(0, o - 1))} disabled={dayOffset === 0} aria-label="Next day" sx={{ color: ACCENT }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, bgcolor: badge.bg, color: badge.fg, borderRadius: '999px', px: 1.25, py: 0.45 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: badge.fg }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{badge.label}</Typography>
          </Box>
        </Stack>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 3 }, alignItems: 'center' }}>
          <TodayMetric icon={<CheckInIcon fontSize="small" />} color="#16a34a" label="Checked in" value={c?.in_time || '—'} />
          <TodayMetric icon={<CheckOutIcon fontSize="small" />} color="#ef4444" label="Checked out" value={c?.out_time || '—'} />
          <TodayMetric icon={<WorkedIcon fontSize="small" />} color={ACCENT} label="Worked" value={worked} />
          <TodayMetric icon={<ShiftIcon fontSize="small" />} color="#8b5cf6" label="Shift" value={c?.shift || 'As per roster'} />
          <Box sx={{ ml: { sm: 'auto' } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, px: 1.25, py: 0.6, borderRadius: '12px', bgcolor: foodOk ? '#f0fdf4' : '#f8fafc', border: `1px solid ${foodOk ? '#dcfce7' : '#eef0f4'}` }}>
              <FoodIcon sx={{ fontSize: 16, color: foodOk ? '#16a34a' : '#9ca3af' }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: foodOk ? '#16a34a' : '#9ca3af' }}>
                {foodOk ? 'Food ₹70 ✓' : foodApplies ? 'Food —' : 'Food ₹0'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  })();

  // One calendar grid for both the 26→25 cycle (arrows page out to full months)
  // and plain month mode.
  const calendarSection = (
    <Paper elevation={0} sx={{ borderRadius: 3, p: 2, pt: 1, border: '1px solid #e0e0e0', width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <IconButton size="small" onClick={isAdminDialog ? adminPrevPeriod : (showCycle ? enterMonthPrev : prevMonth)}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {wantCycle ? (data?.label || 'Attendance cycle') : wantYear ? `Year ${year}` : `${MONTH_NAMES[month - 1]} ${year}`}
          </Typography>
          {!isAdminDialog && isCycleMode && !showCycle && (
            <Typography
              variant="caption"
              onClick={() => setBrowsingMonth(false)}
              sx={{ display: 'block', cursor: 'pointer', color: '#3949ab', fontWeight: 700, '&:hover': { textDecoration: 'underline' } }}
            >
              ← Back to cycle
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={isAdminDialog ? adminNextPeriod : (showCycle ? enterMonthNext : nextMonth)}>
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Stack>
      {wantYear ? (
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center', color: '#9ca3af' }}>
          <Typography variant="body2">
            Day-by-day calendar isn’t shown for a full year. Switch to <b>Cycle</b> or <b>Month</b> for daily punches — the summary, breakdown and insights reflect {year}.
          </Typography>
        </Box>
      ) : (
        <CalendarGrid
          cells={showCycle
            ? formatCycleCells(data?.dates, data?.days)
            : formatDayCells(year, month, data?.days, null, null)}
          onSelect={setSelectedCell}
          roomy={variant !== 'inline'}
          fillHeight={variant !== 'page'}
        />
      )}
      {/* Employee page: the status legend sits at the foot of the calendar card.
          The day tiles are content-sized here (fillHeight=false), so there's room. */}
      {variant === 'page' && !wantYear && (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #f0f0f0' }}>
          {legendBlock}
        </Box>
      )}
    </Paper>
  );

  const bodyBlock = (
    <>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
            <CircularProgress />
          </Box>
        ) : variant === 'inline' ? (
          <Box sx={{ maxWidth: 880, mx: 'auto' }}>
            {calendarSection}
          </Box>
        ) : (
          <Grid container spacing={2.5} alignItems={variant === 'page' ? 'flex-start' : 'stretch'} sx={{ mt: variant === 'page' ? 0 : { xs: 1, md: 2 } }}>
            {/* Left: blue summary + the four insight cards */}
            <Grid item xs={12} md={3}>
              <Stack spacing={2.5}>
                {statsCard}
                <AttendanceInsights data={data} gridProps={{ xs: 6 }} spacing={2} idPrefix={`cal-${employeeNo}`} />
              </Stack>
            </Grid>
            {/* Center: the calendar — the card stretches down to meet the left
                column's bottom (Overtime card). On the employee page the day
                grid itself does NOT stretch its rows (fillHeight=false), so the
                extra height becomes empty space at the bottom of the card rather
                than padding inside each day tile. */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
              {isPage && <Box sx={{ mb: 2.5 }}>{todayStrip}</Box>}
              {calendarSection}
            </Grid>
            {/* Right: attendance breakdown donut + year leave/LOP summary */}
            <Grid item xs={12} md={3} sx={{ alignSelf: 'flex-start' }}>
              <Stack spacing={2.5}>
                <Paper
                  elevation={0}
                  sx={{ p: 1.75, borderRadius: 3, border: '1px solid rgba(229,231,235,0.8)', background: 'rgba(255,255,255,0.9)' }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={800} sx={{ color: '#374151' }}>
                      View period
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontWeight: 700 }}>{periodLabel}</Typography>
                  </Stack>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    fullWidth
                    value={viewMode}
                    onChange={(e, v) => changeViewMode(v)}
                    sx={{ '& .MuiToggleButton-root': { textTransform: 'none', py: 0.4, fontSize: 12, fontWeight: 700 } }}
                  >
                    <ToggleButton value="cycle">Cycle</ToggleButton>
                    <ToggleButton value="month">Month</ToggleButton>
                  </ToggleButtonGroup>
                  <Box sx={{ mt: 1 }}>
                    {wantCycle && (
                      <Select
                        size="small" fullWidth value={activeCycle}
                        onChange={(e) => setActiveCycle(e.target.value)}
                        sx={{ fontSize: 13, fontWeight: 600 }}
                        MenuProps={SELECTED_MENU_PROPS}
                      >
                        {cycleOptions.map((c) => (
                          <MenuItem key={c.cycle} value={c.cycle} sx={{ fontSize: 13 }}>
                            {c.label} · {c.payrollMonthName} {c.payrollYear}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                    {wantMonth && (
                      <Select
                        size="small" fullWidth value={`${year}-${month}`}
                        onChange={(e) => { const [y, m] = e.target.value.split('-').map(Number); setYear(y); setMonth(m); }}
                        sx={{ fontSize: 13, fontWeight: 600 }}
                        MenuProps={SELECTED_MENU_PROPS}
                      >
                        {monthOptions.map((o) => (
                          <MenuItem key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`} sx={{ fontSize: 13 }}>
                            {MONTH_NAMES[o.month - 1]} {o.year}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                    {wantYear && (
                      <Select
                        size="small" fullWidth value={year}
                        onChange={(e) => setYear(Number(e.target.value))}
                        sx={{ fontSize: 13, fontWeight: 600 }}
                        MenuProps={SELECTED_MENU_PROPS}
                      >
                        {yearOptions.map((y) => (
                          <MenuItem key={y} value={y} sx={{ fontSize: 13 }}>{y}</MenuItem>
                        ))}
                      </Select>
                    )}
                  </Box>
                </Paper>
                <AttendanceBreakdownChart data={data} />
                <YearLeaveSummary summary={yearSummary} onClose={onClose} employeeNo={employeeNo} employeeName={data?.name} leavePath={variant === 'page' ? SLUGS.leaves : undefined} />
              </Stack>
            </Grid>
          </Grid>
        )}
    </>
  );

  const detailDrawer = (
      <Drawer
        anchor="right"
        open={Boolean(selectedCell)}
        onClose={closeDayDrawer}
        // This drawer opens on top of the admin dialog (variant='dialog'),
        // which itself sits at theme.zIndex.modal — without an explicit bump
        // the drawer's own default z-index can land behind the dialog, so it
        // "opens" (state is true) but never becomes visible. Same fix as the
        // HRP-FE-19 dialog-vs-fixed-header issue.
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
        PaperProps={{ sx: { width: 360, p: 3 } }}
      >
        {selectedCell && (
          <Box>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              {formatDate(selectedCell.dateStr)}
            </Typography>
            {selectedCell.cell ? (
              <>
                <Stack direction="row" spacing={1} mb={2}>
                  <Chip
                    label={metaForStatus(selectedCell.cell.status, selectedCell.cell.is_half_day).label || selectedCell.cell.status}
                    sx={{
                      bgcolor: metaForStatus(selectedCell.cell.status, selectedCell.cell.is_half_day).bg,
                      color: metaForStatus(selectedCell.cell.status, selectedCell.cell.is_half_day).text,
                      fontWeight: 700,
                    }}
                  />
                  {attendanceSourceLabel(selectedCell.cell) && (
                    <Chip
                      size="small"
                      label={attendanceSourceLabel(selectedCell.cell)}
                      variant="outlined"
                    />
                  )}
                </Stack>
                <DetailRow label="In Time"     value={selectedCell.cell.in_time} />
                <DetailRow label="Out Time"    value={selectedCell.cell.out_time} />
                <DetailRow label="Food Allowance" value={selectedCell.cell.food_allowance_eligible ? '₹70 (eligible)' : foodApplies ? null : '₹0'} />
                <DetailRow label="Shift"       value={selectedCell.cell.shift} />
                <DetailRow label="Work"        value={selectedCell.cell.work_duration} />
                <DetailRow label="OT"          value={selectedCell.cell.overtime} />
                <DetailRow label="Leave type"  value={selectedCell.cell.leave_type} />
                {selectedCell.cell.is_half_day && (
                  <DetailRow label="Leave portion" value={`Half day (${selectedCell.cell.leave_days ?? 0.5} day)`} />
                )}
                <DetailRow label="Notes"       value={selectedCell.cell.notes} />

                {allowOverride && selectedCell.cell.id && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      Manual override
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {['PRESENT', 'ABSENT', 'ON_DUTY', 'COMP_OFF', 'LEAVE', 'HALF_DAY', 'HOLIDAY'].map((s) => (
                        <Button
                          key={s}
                          size="small"
                          variant={s === 'PRESENT' && presentForm ? 'contained' : 'outlined'}
                          disableElevation
                          startIcon={<EditIcon fontSize="small" />}
                          onClick={() => handleOverride(s)}
                          disabled={isOverriding}
                        >
                          {STATUS_META[s]?.label || s}
                        </Button>
                      ))}
                    </Stack>

                    {presentForm && (
                      <Box sx={{ mt: 2, p: 2, borderRadius: '12px', border: '1px solid #dcfce7', bgcolor: '#f7fdf9' }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                          Mark Present — enter the actual timings
                        </Typography>
                        <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
                          <TextField
                            id="override-in-time"
                            label="IN time"
                            type="time"
                            size="small"
                            required
                            fullWidth
                            value={presentForm.in}
                            onChange={(e) => setPresentForm((f) => ({ ...f, in: e.target.value, error: '' }))}
                            error={Boolean(presentForm.error) && !presentForm.in}
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            id="override-out-time"
                            label="OUT time"
                            type="time"
                            size="small"
                            required
                            fullWidth
                            value={presentForm.out}
                            onChange={(e) => setPresentForm((f) => ({ ...f, out: e.target.value, error: '' }))}
                            error={Boolean(presentForm.error) && !presentForm.out}
                            InputLabelProps={{ shrink: true }}
                          />
                        </Stack>
                        {presentForm.error ? (
                          <Typography variant="caption" color="error" role="alert" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
                            {presentForm.error}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            An OUT time earlier than IN is treated as a night shift ending after midnight.
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                          <Button
                            variant="contained"
                            size="small"
                            disableElevation
                            color="success"
                            onClick={savePresentOverride}
                            disabled={isOverriding}
                            startIcon={isOverriding ? <CircularProgress size={14} color="inherit" /> : null}
                          >
                            Save as Present
                          </Button>
                          <Button size="small" onClick={() => setPresentForm(null)} disabled={isOverriding}>
                            Cancel
                          </Button>
                        </Stack>
                      </Box>
                    )}
                  </>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No attendance recorded for this day.
              </Typography>
            )}
          </Box>
        )}
      </Drawer>
  );

  // Full inline page (employee's own attendance dashboard) — the same rich
  // layout as the admin full-screen dialog, but rendered in-page instead of in a
  // Dialog, with manual override hidden and year-summary links disabled.
  // Employee's own attendance page — the ATS mockup layout. Reuses the shared
  // data/logic (statList, buildAttendanceMetrics, todayStrip, the breakdown donut
  // and the year summary); only the presentation is rebuilt. Scoped to this
  // variant, so the admin dialog/inline views are untouched.
  if (variant === 'page') {
    const metrics = buildAttendanceMetrics(data);
    const workHrs = fmtHoursMinutes(metrics.workMin);
    const otHrs = fmtHoursMinutes(metrics.otMin);
    const lateHrs = fmtHoursMinutes(metrics.lateMin);
    const pageCells = wantYear
      ? []
      : showCycle
      ? formatCycleCells(data?.dates, data?.days)
      : formatDayCells(year, month, data?.days, null, null);
    const fillers = (3 - (statList.length % 3)) % 3;
    const pageSub = wantCycle
      ? `Cycle ${data?.label || ''}${data?.payroll_month ? ` · Payroll ${data.payroll_month} ${data.payroll_year}` : ''}`
      : wantMonth ? `${MONTH_NAMES[month - 1]} ${year}` : `Year ${year}`;
    const selectSx = { fontSize: 13, fontWeight: 600, borderRadius: '11px', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E3E6EC' } };
    const segSx = {
      bgcolor: '#f1f3f6', borderRadius: '11px', p: '3px',
      '& .MuiToggleButton-root': { border: 'none', borderRadius: '8px !important', textTransform: 'none', py: 0.6, fontSize: 13, fontWeight: 600, color: '#586173' },
      '& .Mui-selected': { bgcolor: '#fff !important', color: '#0E1420 !important', boxShadow: '0 1px 2px rgba(16,24,40,.05)' },
    };

    return (
      <Box sx={{
        '& .MuiTypography-root': { fontWeight: 600 },
        // Normalise every card on the page to the mockup's 18px radius — this also
        // reins in the reused breakdown/year Papers (borderRadius:3 → 48px via the
        // theme). Scoped to the page, so the admin dialog/inline views are untouched.
        '& .MuiPaper-root': { borderRadius: '18px' },
      }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>
        ) : (
          <>
            {/* Page header */}
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: { xs: '1.3rem', sm: '1.5rem' }, fontWeight: 600, letterSpacing: '-0.02em', color: '#0E1420', lineHeight: 1.2 }}>Attendance</Typography>
              <Typography sx={{ fontSize: '0.82rem', color: '#8A94A4', fontWeight: 500, mt: 0.4 }}>{pageSub}</Typography>
            </Box>

            <Grid container spacing={2.5} alignItems="flex-start">
              {/* LEFT — cycle summary + mini metrics */}
              <Grid item xs={12} md={3}>
                <Stack spacing={2.5}>
                  <Paper elevation={0} sx={PAGE_CARD_SX}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily: NUM_FONT, fontSize: '1.1rem', fontWeight: 600, color: '#0E1420', lineHeight: 1.1 }}>{headerTitle}</Typography>
                        {headerSub && <Typography sx={{ fontSize: '0.72rem', color: '#8A94A4', fontWeight: 500, mt: 0.3 }}>{headerSub}</Typography>}
                      </Box>
                      <Box sx={{ fontSize: '0.66rem', fontWeight: 600, color: '#059669', bgcolor: '#ECFDF5', px: 1, py: 0.5, borderRadius: '7px', whiteSpace: 'nowrap' }}>
                        {wantCycle ? 'Cycle' : wantMonth ? 'Month' : 'Year'}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', bgcolor: '#eceef2', border: '1px solid #eceef2', borderRadius: '14px', overflow: 'hidden' }}>
                      {statList.map((s) => <CycleStat key={s.label} label={s.label} value={s.value} dot={STAT_DOT[s.label]} onClick={s.onClick} />)}
                      {Array.from({ length: fillers }).map((_, i) => <Box key={`f${i}`} sx={{ bgcolor: '#fafbfc' }} />)}
                    </Box>
                  </Paper>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.75 }}>
                    <MiniMetric icon={<OnTimeIcon sx={{ fontSize: 16 }} />} iconBg="#ECFDF5" iconColor="#059669" label="On-Time %" value={`${metrics.onTimePct}%`} accent="#10B981" />
                    <MiniMetric icon={<LateIcon sx={{ fontSize: 16 }} />} iconBg="#FEF2F2" iconColor="#EF4444" label="Late %" value={`${metrics.latePct}%`} accent="#EF4444" />
                    <MiniMetric compact icon={<WorkedIcon sx={{ fontSize: 16 }} />} iconBg="#EFF6FF" iconColor="#3B82F6" label="Working Hours" value={`${workHrs.h}h ${workHrs.m}m`} cap="this cycle" />
                    <MiniMetric compact icon={<OvertimeIcon sx={{ fontSize: 16 }} />} iconBg="#FFFBEB" iconColor="#D97706" label="Overtime Hours" value={`${otHrs.h}h ${otHrs.m}m`} cap="this cycle" />
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <MiniMetric
                        compact
                        icon={<LateIcon sx={{ fontSize: 16 }} />}
                        iconBg="#FEF2F2"
                        iconColor="#EF4444"
                        label="Late Login Hours"
                        value={`${lateHrs.h}h ${String(lateHrs.m).padStart(2, '0')}m`}
                        accent="#EF4444"
                        cap="this cycle"
                      />
                    </Box>
                  </Box>
                </Stack>
              </Grid>

              {/* CENTER — yesterday strip + calendar */}
              <Grid item xs={12} md={6}>
                <Stack spacing={2.5}>
                  {todayStrip}
                  <Paper elevation={0} sx={PAGE_CARD_SX}>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 2 }}>
                      <IconButton size="small" onClick={adminPrevPeriod} sx={{ border: '1px solid #E3E6EC' }}><ChevronLeftIcon fontSize="small" /></IconButton>
                      <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, minWidth: 150, textAlign: 'center' }}>
                        {wantCycle ? (data?.label || 'Attendance cycle') : wantYear ? `Year ${year}` : `${MONTH_NAMES[month - 1]} ${year}`}
                      </Typography>
                      <IconButton size="small" onClick={adminNextPeriod} sx={{ border: '1px solid #E3E6EC' }}><ChevronRightIcon fontSize="small" /></IconButton>
                    </Stack>
                    {wantYear ? (
                      <Box sx={{ py: 5, textAlign: 'center', color: '#9ca3af' }}>
                        <Typography variant="body2">Switch to <b>Cycle</b> or <b>Month</b> for the day-by-day grid — the summary, breakdown and insights still reflect {year}.</Typography>
                      </Box>
                    ) : (
                      <>
                        {/* minmax(0, 1fr), not 1fr: a plain 1fr is minmax(auto, 1fr), so a tile whose
                            content is wider than its share (e.g. "P 10:04") stretches its own column.
                            The header row has no such content, so its seven columns stayed equal while
                            the day grid's did not - the labels stopped lining up with the tiles and the
                            Sunday column was pushed to the screen edge and clipped, which reads as a
                            missing Sunday on a phone (CRM #48745). */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.75, sm: 1 }, mb: 1 }}>
                          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => (
                            <Typography key={d} sx={{ textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#8A94A4' }}>{d}</Typography>
                          ))}
                        </Box>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: { xs: 0.75, sm: 1 } }}>
                          {pageCells.map((c, i) => <PageCalCell key={i} cell={c} onClick={(pc) => {
                            // Employee page: clicking an ABSENT day goes straight to the
                            // leave request with that date pre-filled & locked. Any other
                            // status opens the normal day-detail drawer.
                            if (String(pc?.cell?.status || '').toUpperCase().trim() === 'ABSENT') {
                              navigate(`${SLUGS.leaves}?apply=1&date=${pc.dateStr}`);
                              return;
                            }
                            setSelectedCell(pc);
                          }} />)}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2.25, pt: 2, borderTop: '1px solid #eceef2' }}>
                          {[['P', 'Present'], ['A', 'Absent'], ['L', 'On Leave'], ['½L', 'Half Day Leave'], ['OD', 'On Duty'], ['CO', 'Comp Off'], ['H', 'Holiday'], ['HD', 'Half Day']].map(([code, label]) => (
                            <Box key={code} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: '0.7rem', fontWeight: 600, color: '#586173', bgcolor: '#fafbfc', border: '1px solid #eceef2', px: 1.25, py: 0.55, borderRadius: '999px' }}>
                              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: LEGEND_COLOR[code] }} />{code} · {label}
                            </Box>
                          ))}
                        </Box>
                      </>
                    )}
                  </Paper>
                </Stack>
              </Grid>

              {/* RIGHT — view period + breakdown + year summary */}
              <Grid item xs={12} md={3}>
                <Stack spacing={2.5}>
                  <Paper elevation={0} sx={PAGE_CARD_SX}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                      <Typography sx={{ fontSize: '0.68rem', letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8A94A4', fontWeight: 600 }}>View period</Typography>
                      <Typography sx={{ fontFamily: NUM_FONT, fontSize: '0.72rem', color: '#8A94A4', fontWeight: 600 }}>{periodLabel}</Typography>
                    </Box>
                    <ToggleButtonGroup size="small" exclusive fullWidth value={viewMode} onChange={(e, v) => changeViewMode(v)} sx={{ mb: 1.5, ...segSx }}>
                      <ToggleButton value="cycle">Cycle</ToggleButton>
                      <ToggleButton value="month">Month</ToggleButton>
                    </ToggleButtonGroup>
                    {wantCycle && (
                      <Select size="small" fullWidth value={activeCycle} onChange={(e) => setActiveCycle(e.target.value)} sx={selectSx} MenuProps={SELECTED_MENU_PROPS}>
                        {cycleOptions.map((c) => (
                          <MenuItem key={c.cycle} value={c.cycle} sx={{ fontSize: 13 }}>{c.label} · {c.payrollMonthName} {c.payrollYear}</MenuItem>
                        ))}
                      </Select>
                    )}
                    {wantMonth && (
                      <Select size="small" fullWidth value={`${year}-${month}`} onChange={(e) => { const [y, m] = e.target.value.split('-').map(Number); setYear(y); setMonth(m); }} sx={selectSx} MenuProps={SELECTED_MENU_PROPS}>
                        {monthOptions.map((o) => (
                          <MenuItem key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`} sx={{ fontSize: 13 }}>{MONTH_NAMES[o.month - 1]} {o.year}</MenuItem>
                        ))}
                      </Select>
                    )}
                    {wantYear && (
                      <Select size="small" fullWidth value={year} onChange={(e) => setYear(Number(e.target.value))} sx={selectSx} MenuProps={SELECTED_MENU_PROPS}>
                        {yearOptions.map((y) => <MenuItem key={y} value={y} sx={{ fontSize: 13 }}>{y}</MenuItem>)}
                      </Select>
                    )}
                  </Paper>

                  <AttendanceBreakdownChart data={data} />
                  <YearLeaveSummary summary={yearSummary} onClose={onClose} employeeNo={employeeNo} employeeName={data?.name} leavePath={SLUGS.leaves} />
                </Stack>
              </Grid>
            </Grid>
          </>
        )}
        {detailDrawer}
      </Box>
    );
  }

  if (variant === 'inline') {
    return (
      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: '1px solid #e0e0e0', bgcolor: '#fff', overflow: 'hidden', height: '100%' }}
      >
        <Box sx={{ p: 2.5, bgcolor: '#fff', borderBottom: '1px solid #e0e0e0', position: 'sticky', top: 0, zIndex: 1 }}>
          {titleBlock}
        </Box>
        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          {bodyBlock}
        </Box>
        {detailDrawer}
      </Paper>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{ sx: { bgcolor: '#F7F9FB' } }}
    >
      {/* Offset below the fixed admin header (64px, z-index 1301) — this
          full-screen dialog is z-index 1300, so without this the title row
          (employee name + designation) hides behind the header. */}
      <DialogTitle sx={{ pt: '76px', pb: 1, bgcolor: '#fff', borderBottom: '1px solid #e0e0e0' }}>
        {titleBlock}
      </DialogTitle>
      <DialogContent sx={{ p: 4 }}>
        {bodyBlock}
      </DialogContent>
      {detailDrawer}
    </Dialog>
  );
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Weekday header + a 7-column grid of day cells (nulls render as empty spacers).
const CalendarGrid = ({ cells, onSelect, roomy = false, fillHeight = true }) => (
  <>
    <Grid container columns={7} spacing={0} sx={{ mb: roomy ? 1.25 : 0.75 }}>
      {WEEKDAYS.map((d) => (
        <Grid item xs={1} key={d}>
          <Typography
            variant="caption"
            sx={{ display: 'block', textAlign: 'center', fontWeight: 700, color: 'text.secondary', py: 0.5, fontSize: roomy ? 12 : 10.5 }}
          >
            {roomy ? d : d.slice(0, 1)}
          </Typography>
        </Grid>
      ))}
    </Grid>
    {/* fillHeight: when true the rows stretch to fill the card (admin dialog);
        when false the rows stay content-sized and the card's spare height shows
        as empty space at the bottom instead of padding inside the tiles. */}
    <Grid container columns={7} spacing={roomy ? 1 : 0.5} sx={{ flexGrow: fillHeight && roomy ? 1 : 0, alignContent: fillHeight ? 'stretch' : 'flex-start' }}>
      {cells.map((c, i) => (
        <Grid item xs={1} key={i}>
          {c ? (
            <CalendarCell cell={c} onClick={() => onSelect(c)} roomy={roomy} />
          ) : (
            <Box sx={{ height: '100%' }} />
          )}
        </Grid>
      ))}
    </Grid>
  </>
);

const CalendarCell = ({ cell, onClick, roomy = false }) => {
  // Sundays are always holidays — show "H" even for days with no record
  // (e.g. out-of-cycle Sundays) so every Sunday looks the same.
  const [yy, mm, dd] = cell.dateStr.split('-').map(Number);
  const isSunday = new Date(yy, mm - 1, dd).getDay() === 0;
  const todayStr = new Date().toISOString().slice(0, 10);
  const isFuture = cell.dateStr > todayStr;
  // Sunday is always a holiday — a schedule fact, not an attendance
  // observation — so it must win even for a future date, before the
  // isFuture fallback (which otherwise forces every future day to NR).
  const status = isSunday ? 'HOLIDAY' : (isFuture ? 'NOT_RECORDED' : (cell.cell?.status || 'NOT_RECORDED'));
  const meta = metaForStatus(status, cell.cell?.is_half_day);
  const inTime = cell.cell?.in_time;
  const outTime = cell.cell?.out_time;
  const shift = cell.cell?.shift;
  const workDur = fmtDur(cell.cell?.work_duration);
  const isToday = todayStr === cell.dateStr;
  const isManualOverride = cell.cell?.source === 'MANUAL_OVERRIDE';

  const tooltipParts = [meta.label];
  if (inTime) tooltipParts.push(`in ${inTime}`);
  if (outTime) tooltipParts.push(`out ${outTime}`);
  if (workDur) tooltipParts.push(`work ${workDur}`);
  if (shift) tooltipParts.push(shift);
  if (isManualOverride) tooltipParts.push('manually updated');

  return (
    <Tooltip
      title={tooltipParts.join(' · ')}
      arrow
      placement="top"
    >
      <Box
        onClick={isFuture ? undefined : onClick}
        sx={{
          position: 'relative',
          aspectRatio: roomy ? undefined : '1 / 1',
          minHeight: roomy ? { xs: 58, sm: 64 } : undefined,
          height: roomy ? '100%' : undefined,
          borderRadius: 1,
          bgcolor: meta.bg,
          color: meta.text,
          p: roomy ? 0.7 : 0.25,
          cursor: isFuture ? 'default' : 'pointer',
          border: isToday ? '2px solid #3DAC5A' : '1px solid transparent',
          transition: 'transform 120ms ease, box-shadow 120ms ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
          },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: roomy ? 'flex-start' : 'center',
          alignItems: roomy ? 'flex-start' : 'center',
          textAlign: roomy ? 'left' : 'center',
          minWidth: 0,
        }}
      >
        {isManualOverride && (
          <EditIcon
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              fontSize: roomy ? 13 : 10,
              opacity: 0.65,
            }}
          />
        )}
        <Typography sx={{ fontSize: roomy ? 13 : 10.5, fontWeight: 700, lineHeight: 1, opacity: 0.85 }}>
          {cell.day}{dd === 1 ? ` ${new Date(yy, mm - 1, 1).toLocaleString('en-US', { month: 'short' })}` : ''}
        </Typography>
        <Typography sx={{ fontSize: roomy ? 18 : 12.5, fontWeight: 800, lineHeight: 1.05, mt: 0.1 }}>
          {meta.code}
        </Typography>
        {(inTime || outTime) && (
          <Box sx={{ mt: 0.15 }}>
            <Typography component="div" sx={{ fontSize: roomy ? { xs: 10, sm: 11.5 } : 9.5, fontWeight: 600, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
              <Box component="span" sx={{ opacity: 0.6 }}>In</Box> {inTime || '—'}
            </Typography>
            <Typography component="div" sx={{ fontSize: roomy ? { xs: 10, sm: 11.5 } : 9.5, fontWeight: 600, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
              <Box component="span" sx={{ opacity: 0.6 }}>Out</Box> {outTime || '—'}
            </Typography>
            {roomy && workDur && (
              <Typography component="div" sx={{ fontSize: { xs: 10, sm: 11.5 }, fontWeight: 700, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
                <Box component="span" sx={{ opacity: 0.6 }}>Work</Box> {workDur}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

// Employee page (onLight) → bordered mockup-style tile with tabular numbers.
// Admin dialog/inline (default) → original centred white-on-purple figure.
const SummaryStat = ({ label, value, dot, onLight }) =>
  onLight ? (
    <Box sx={{ border: '1px solid #eef0f4', borderRadius: '12px', p: 1.25, bgcolor: '#fafbfc', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, fontSize: '1.25rem', lineHeight: 1, color: '#111827' }}>{value}</Typography>
        {dot && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />}
      </Box>
      <Typography sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.68rem', mt: 0.75, display: 'block' }}>{label}</Typography>
    </Box>
  ) : (
    <Box sx={{ textAlign: 'center', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        <Typography variant="h5" fontWeight={800} sx={{ color: 'inherit' }}>{value}</Typography>
      </Box>
      <Typography variant="caption" sx={{ opacity: 0.85, color: 'inherit' }}>{label}</Typography>
    </Box>
  );

// One metric in the "Today at a glance" strip: soft icon tile + label/value.
const TodayMetric = ({ icon, label, value, color = '#6b7280' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
    <Box sx={{
      width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
      bgcolor: `${color}14`, color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', lineHeight: 1.1, fontSize: '0.62rem' }}>{label}</Typography>
      <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '0.92rem', lineHeight: 1.3, whiteSpace: 'nowrap' }}>{value}</Typography>
    </Box>
  </Box>
);

const DetailRow = ({ label, value }) =>
  value ? (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={600}>{value}</Typography>
    </Stack>
  ) : null;

/* ------------------------------------------------------------------ *
 * Employee-page (mockup) presentational pieces. Pure presentation —
 * all values are fed from the same data/logic as the admin views.
 * ------------------------------------------------------------------ */

// Divided cycle-summary cell (mockup `.si`): value + status dot + label.
// When `onClick` is supplied the tile becomes an actionable button (e.g. the
// LOP-days tile opens the Apply-for-Leave modal) — pointer cursor, hover tint,
// a subtle "Apply leave" affordance and keyboard support.
const CycleStat = ({ label, value, dot, onClick }) => {
  const clickable = typeof onClick === 'function';
  const tile = (
    <Box
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      sx={{
        bgcolor: '#fff', px: 1.5, py: 1.5, height: '100%',
        ...(clickable && {
          cursor: 'pointer',
          transition: 'background-color .15s',
          '&:hover': { bgcolor: '#f2f6ff' },
          '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: '-2px' },
        }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, fontSize: '1.3rem', lineHeight: 1, color: '#0E1420' }}>{value}</Typography>
        {dot && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />}
      </Box>
      <Typography sx={{ fontSize: '0.7rem', color: clickable ? ACCENT : '#586173', fontWeight: clickable ? 600 : 500, mt: 0.85, lineHeight: 1.25 }}>
        {clickable ? `${label} · Apply ›` : label}
      </Typography>
    </Box>
  );
  return clickable ? <Tooltip arrow title="Apply for leave">{tile}</Tooltip> : tile;
};

// Mini metric card (mockup `.mini`): icon + label, big value, accent bar/caption.
const MiniMetric = ({ icon, iconBg, iconColor, label, value, accent, cap, compact = false }) => (
  <Paper elevation={0} sx={{ ...PAGE_CARD_SX, borderRadius: '14px', p: compact ? 1.4 : 1.75 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 30, height: 30, borderRadius: '9px', bgcolor: iconBg, color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</Box>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#586173', lineHeight: 1.2 }}>{label}</Typography>
    </Box>
    <Typography sx={{ fontFamily: NUM_FONT, fontWeight: 600, fontSize: compact ? '1.35rem' : '1.55rem', mt: compact ? 1.05 : 1.5, lineHeight: 1, color: '#0E1420' }}>{value}</Typography>
    {accent && <Box sx={{ height: 3, borderRadius: '20px', mt: compact ? 0.9 : 1.25, width: '60%', bgcolor: accent }} />}
    {cap && <Typography sx={{ fontSize: '0.68rem', color: '#8A94A4', fontWeight: 500, mt: compact ? 0.7 : 1 }}>{cap}</Typography>}
  </Paper>
);

// One day tile in the employee-page calendar (mockup `.cd`). Clickable → opens
// the shared day-detail drawer; colours/labels come from STATUS_META/STATUS_DOT.
const PageCalCell = ({ cell, onClick }) => {
  if (!cell) return <Box sx={{ minHeight: { xs: 54, sm: 64 }, minWidth: 0 }} />;
  const [yy, mm, dd] = cell.dateStr.split('-').map(Number);
  const isSunday = new Date(yy, mm - 1, dd).getDay() === 0;
  const todayStr = new Date().toISOString().slice(0, 10);
  const isToday = todayStr === cell.dateStr;
  const isFuture = cell.dateStr > todayStr;
  // Sunday is always a holiday — a schedule fact, not an attendance
  // observation — so it must win even for a future date, before the
  // isFuture fallback (which otherwise forces every future day to NR).
  const status = isSunday ? 'HOLIDAY' : (isFuture ? 'NOT_RECORDED' : (cell.cell?.status || null));
  const half = status === 'LEAVE' && cell.cell?.is_half_day;
  // Past day, no attendance/leave record at all: label it NOT_RECORDED
  // instead of a bare dash. Kept distinct from ABSENT/LOP on purpose — a
  // missing daily upload must not read as (or dock pay like) an absence;
  // see the same rule enforced in lop_utils.compute_lop_for_cycle.
  const notRecorded = !status && !isToday && !isFuture;
  const meta = status
    ? metaForStatus(status, cell.cell?.is_half_day)
    : (notRecorded ? STATUS_META.NOT_RECORDED : undefined);
  const dotKey = half ? 'HALF_DAY_LEAVE' : status;
  const isHoliday = status === 'HOLIDAY';
  const inTime = cell.cell?.in_time;
  const monthTag = dd === 1 ? new Date(yy, mm - 1, 1).toLocaleString('en-US', { month: 'short' }) : '';
  const lineColor = isHoliday ? '#94a3b8' : (status ? (STATUS_DOT[dotKey] || '#586173') : (notRecorded ? STATUS_DOT.NOT_RECORDED : (isToday ? ACCENT : '#8A94A4')));
  const statusText = meta?.code ? (inTime ? `${meta.code} ${inTime}` : meta.code) : (inTime || '');
  const lineText = isHoliday ? 'Holiday' : (status ? statusText : (notRecorded ? meta.code : (isToday ? 'Today' : '—')));
  // Seven equal columns on a 390px phone leave ~40px per tile, so the full label
  // ("P 10:04", "Holiday") cannot fit. Show the status CODE alone on xs and the
  // full label from sm up; the punch times are one tap away in the day drawer.
  const shortText = isHoliday ? 'H' : (status ? (meta?.code || '') : (notRecorded ? meta.code : '—'));
  const tip = [meta?.label, inTime ? `in ${inTime}` : null, cell.cell?.out_time ? `out ${cell.cell.out_time}` : null].filter(Boolean).join(' · ');
  return (
    <Tooltip title={tip || ''} arrow placement="top">
      <Box
        onClick={() => onClick(cell)}
        sx={{
          // minWidth: 0 so the tile can shrink to its column - without it the tile's
          // own min-content width sets the track minimum again (CRM #48745).
          minHeight: { xs: 54, sm: 64 }, minWidth: 0, overflow: 'hidden',
          borderRadius: '12px', p: { xs: 0.75, sm: 1.1 }, cursor: 'pointer',
          bgcolor: isHoliday ? '#f1f3f6' : '#fff',
          border: isToday ? `1px solid ${ACCENT}` : (isHoliday ? '1px solid transparent' : '1px solid #eceef2'),
          boxShadow: isToday ? `0 0 0 1px ${ACCENT}` : 'none',
          display: 'flex', flexDirection: 'column',
          transition: 'border-color .15s, background .15s',
          '&:hover': { borderColor: '#E3E6EC', bgcolor: isHoliday ? '#f1f3f6' : '#fafbfc' },
        }}
      >
        <Typography sx={{ fontFamily: NUM_FONT, fontSize: '0.85rem', fontWeight: 600, color: isToday ? ACCENT : '#0E1420', lineHeight: 1 }}>
          {dd}{monthTag && <Box component="span" sx={{ fontSize: '0.6rem', color: '#8A94A4', ml: 0.4 }}>{monthTag}</Box>}
        </Typography>
        <Box sx={{ mt: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
          {(isHoliday || (status && !inTime) || notRecorded) && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: lineColor, flexShrink: 0 }} />}
          <Typography sx={{ fontFamily: inTime ? NUM_FONT : 'inherit', fontSize: { xs: '0.68rem', sm: '0.72rem' }, fontWeight: 600, color: lineColor, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{shortText}</Box>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{lineText}</Box>
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default EmployeeAttendanceCalendar;
